/**
 * Minimal IndexedDB store for the offline workout queue.
 *
 * WHY INDEXEDDB. The Capacitor shell loads the live site, and offline.js
 * reloads the page after an outage, so anything only in memory is lost on
 * reconnect, and everything is lost if the phone kills the app at the gym.
 * IndexedDB survives both, and stores a recorded video as a Blob on disk
 * (WebKit and Chromium both spill large Blobs to files rather than holding
 * them in memory), so a 200 MB clip never has to be read into JS to be saved.
 *
 * Two stores:
 *   - requests: queued set saves ({ id, coalesceKey, userId, ... })
 *   - videos:   recorded form videos ({ id, blob, userId, target, ... })
 *
 * Every function resolves; none throws for "IndexedDB is unavailable" (old
 * WebView, private mode, quota). Callers learn that from isOfflineDbAvailable()
 * and leave the request to fail the old way rather than pretend it was saved.
 */

const DB_NAME = "tkc_offline_v1";
const DB_VERSION = 1;
export const REQUESTS_STORE = "requests";
export const VIDEOS_STORE = "videos";

let dbPromise = null;

const promisify = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export const isOfflineDbAvailable = () =>
  typeof indexedDB !== "undefined" && indexedDB !== null;

export function openOfflineDb() {
  if (!isOfflineDbAvailable()) {
    return Promise.reject(new Error("IndexedDB unavailable"));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(REQUESTS_STORE)) {
          db.createObjectStore(REQUESTS_STORE, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(VIDEOS_STORE)) {
          db.createObjectStore(VIDEOS_STORE, { keyPath: "id" });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        // Another tab upgrading the schema must not be blocked by this one.
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error("IndexedDB open blocked"));
    }).catch((error) => {
      dbPromise = null; // allow a retry on the next call
      throw error;
    });
  }
  return dbPromise;
}

async function withStore(storeName, mode, run) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    let result;
    Promise.resolve(run(store))
      .then((value) => {
        result = value;
      })
      .catch(reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("transaction aborted"));
  });
}

export const getAll = (storeName) =>
  withStore(storeName, "readonly", (store) => promisify(store.getAll()));

export const putRecord = (storeName, record) =>
  withStore(storeName, "readwrite", (store) => promisify(store.put(record)));

export const deleteRecord = (storeName, id) =>
  withStore(storeName, "readwrite", (store) => promisify(store.delete(id)));

/**
 * Read-modify-write inside ONE transaction, so two enqueues racing on the same
 * set cannot both read "no entry" and both append. `mutate(records)` returns
 * { put: [...records], remove: [...ids], result }.
 */
export const transact = (storeName, mutate) =>
  withStore(storeName, "readwrite", async (store) => {
    const records = await promisify(store.getAll());
    const { put = [], remove = [], result } = mutate(records) || {};
    remove.forEach((id) => store.delete(id));
    put.forEach((record) => store.put(record));
    return result;
  });

// Tests only: drop the cached connection so each spec opens a fresh database.
export const resetOfflineDbForTests = () => {
  dbPromise = null;
};
